import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Spinner from "../../../component/page/loading/spinner";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import docAid from "../../../assets/doc-aid.png";

const OtpCheck = () => {
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from location state (or from localStorage OTP data)
  const email = location.state?.getEmail || "";

  const [timer, setTimer] = useState(600);
  const [timerCompleted, setTimerCompleted] = useState(false);

  // On mount, read OTP data from localStorage
  const otpData = JSON.parse(localStorage.getItem("userOTP"));

  // If no OTP data or email mismatch, show error and redirect
  useEffect(() => {
    if (!otpData || (email && otpData.email !== email)) {
      toast.error("No OTP request found. Please regenerate OTP.");
      navigate("/regenrateOtp"); // or appropriate route
    }
  }, []);

  // Timer logic
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setTimerCompleted(true);
    }
  }, [timer]);

  const formik = useFormik({
    initialValues: {
      otp: "",
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object({
      otp: Yup.string()
        .matches(/^\d{4}$/, "OTP must be 4 digits")
        .required("Please enter OTP"),
    }),
    onSubmit: (values) => {
      verifyOtp(values.otp);
    },
  });

  const verifyOtp = (enteredOtp) => {
    setLoadingSpinner(true);

    // Get latest OTP data from localStorage
    const latestOtpData = JSON.parse(localStorage.getItem("userOTP"));

    if (!latestOtpData) {
      toast.error("OTP expired. Please regenerate.");
      setLoadingSpinner(false);
      navigate("/regenrateOtp");
      return;
    }

    // Compare OTP (convert both to string)
    if (String(latestOtpData.otp) === String(enteredOtp)) {
      // Success - clear OTP and navigate
      localStorage.removeItem("userOTP");
      toast.success("OTP verified successfully!");
      setLoadingSpinner(false);
      // Navigate to reset password page with email
      navigate("/forgotNewPassword", { state: { email: latestOtpData.email } });
    } else {
      toast.error("Invalid OTP. Please try again.");
      setLoadingSpinner(false);
    }
  };

  const handleResendOtp = () => {
    // Navigate to regenerate OTP page with email
    navigate("/regenrateOtp", { state: { getEmail: email } });
  };

  return (
    <>
      <div className="min-h-screen bg-gray-100 text-gray-900 flex flex-col items-center justify-between">
        <div className="max-w-screen-xl m-0 sm:m-10 md:shadow sm:rounded-lg flex justify-center flex-1">
          <div className="lg:flex-1 md:bg-white text-center flex flex-col justify-center items-center">
            <div className="w-full flex justify-center items-center">
              <div className="lg:w-3/4 mb-24 md:mb-0">
                <img
                  alt="img"
                  className="w-[350px] xl:w-full lg:w-auto mb-24 md:mb-0"
                  src={docAid}
                />
              </div>
            </div>
          </div>
          {/* Single Form – responsive */}
          <div
            className="lg:w-1/2 xl:w-5/12 p-6 sm:p-12 flex items-center"
            style={{ backgroundColor: "rgb(237, 231, 242)" }}
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                formik.handleSubmit();
              }}
              className="w-full"
            >
              <div className="mx-auto max-w-xs">
                <p className="text-center text-gray-600 mb-4">
                  OTP sent to <strong>{email || "your email"}</strong>
                </p>

                <input
                  className="w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="otp"
                  value={formik.values.otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "");
                    formik.setFieldValue("otp", val);
                  }}
                  onBlur={formik.handleBlur}
                  placeholder="Enter 4-digit OTP"
                  maxLength="4"
                  autoComplete="one-time-code"
                />
                {formik.touched.otp && formik.errors.otp && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.otp}</p>
                )}

                <div className="flex justify-end items-center mt-3">
                  {!timerCompleted ? (
                    <p className="text-red-500">
                      {String(Math.floor(timer / 60)).padStart(2, "0")}:
                      {String(timer % 60).padStart(2, "0")} Min
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="text-blue-500 hover:text-blue-700"
                      onClick={handleResendOtp}
                    >
                      Resend OTP
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loadingSpinner}
                  className="mt-5 tracking-wide font-semibold text-white w-full py-4 rounded-lg transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none disabled:opacity-50"
                  style={{ backgroundColor: "rgb(7, 160, 165)" }}
                >
                  {loadingSpinner ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {loadingSpinner && <Spinner />}
      <ToastContainer />
    </>
  );
};

export default OtpCheck;